import React from "react";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import AddIcon from "@material-ui/icons/Add";
import RefreshIcon from "@material-ui/icons/Refresh";

import Button from "../../../../../../components/common/PL/Button/Button";
import AutorizacionEgresoGroupCard from "./AutorizacionEgresoGroupCard";
import SolicitarAutorizacionEgresoModal, {
  SolicitarAutorizacionEgresoSubmitData,
} from "./SolicitarAutorizacionEgresoModal";
import {
  ExitAuthorization,
  ExitAuthorizationGroup,
  GateControlKey,
  GateControlObservation,
  TransportExitControls,
  isExitControlKey,
  isPlateControlKey,
} from "./transportAuthorizationTypes";
import { EntranceVehiclePrefill } from "./transportAuthorizationMappers";
import { useElapsedSeconds } from "./useElapsedSeconds";

const REFRESH_AVAILABLE_AFTER_SECONDS = 10;

type AutorizacionesEgresoListProps = {
  groups: ExitAuthorizationGroup[];
  isLoading: boolean;
  selectedAutorizacionId: string | null;
  onSelectAutorizacion: (auth: ExitAuthorization) => void;
  onResend: (authId: string) => void;
  onRegistrarPorOtroMedio: (authId: string) => void;
  onRequestAuthorization: (data: SolicitarAutorizacionEgresoSubmitData) => Promise<boolean>;
  /** Empresa que autorizó el ingreso, para prefillear el modal de solicitud. */
  prefillCompanyId?: string;
  /** Patentes del ingreso, para prefillear el modal de solicitud. */
  prefillVehicles?: EntranceVehiclePrefill;
  /** Remito de Observaciones, para prefillear el modal de solicitud. */
  prefillRemito?: string;
  onRefresh: () => void;
  /** Espeja los controles de la autorización seleccionada para que el submit pueda mandarlos. */
  onControlsChange: (controls: TransportExitControls | null) => void;
  disabled?: boolean;
};

/** authId -> controles que el guardia ya verificó. No se persiste: no hay endpoint todavía. */
type VerifiedControls = Record<string, Partial<Record<GateControlKey, boolean>>>;

/** authId -> observación (nota + foto) que el guardia dejó por control. Sobrevive a la
 * deselección de la autorización: lo cargado a mano no se descarta solo, y viaja en el submit
 * (`transportExitControls`) cuando la autorización queda seleccionada. */
type ControlObservations = Record<string, Partial<Record<GateControlKey, GateControlObservation>>>;

const AutorizacionesEgresoList: React.FC<AutorizacionesEgresoListProps> = ({
  groups,
  isLoading,
  selectedAutorizacionId,
  onSelectAutorizacion,
  onResend,
  onRegistrarPorOtroMedio,
  onRequestAuthorization,
  prefillCompanyId,
  prefillVehicles,
  prefillRemito,
  onRefresh,
  onControlsChange,
  disabled = false,
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [expandedGrupoId, setExpandedGrupoId] = React.useState<string | null>(null);
  const [expandedAutorizacionId, setExpandedAutorizacionId] = React.useState<string | null>(null);
  const [verifiedControls, setVerifiedControls] = React.useState<VerifiedControls>({});
  const [controlObservations, setControlObservations] = React.useState<ControlObservations>({});
  const [lastRefreshAt, setLastRefreshAt] = React.useState<number | null>(null);
  const elapsedSinceRefresh = useElapsedSeconds(lastRefreshAt ?? 0);
  const canRefresh =
    lastRefreshAt === null || elapsedSinceRefresh >= REFRESH_AVAILABLE_AFTER_SECONDS;

  const handleRefreshClick = () => {
    if (!canRefresh) return;
    setLastRefreshAt(Date.now());
    onRefresh();
  };

  // Una solicitud creada por el propio guardia y ya aprobada no necesita reverificarse: fue el
  // guardia quien cargó patente/remito/etc. al pedirla. Las preautorizaciones (armadas por un
  // autorizante externo que el guardia nunca vio) sí siguen requiriendo el chequeo manual.
  const isGuardApprovedRequest = (auth: ExitAuthorization) =>
    auth.kind === "request" && auth.status === "approved";

  // La verificación se superpone a los datos traídos del backend en lugar de mutarlos: así un
  // refetch reemplaza las autorizaciones y los tildes siguen sobre la autorización correcta. El
  // guardia puede seguir des/re-verificando manualmente incluso cuando parte por default en true:
  // esa acción explícita siempre gana sobre el default.
  const groupsWithVerification = React.useMemo(
    () =>
      groups.map((group) => ({
        ...group,
        authorizations: group.authorizations.map((auth) => {
          const defaultVerified = isGuardApprovedRequest(auth);
          return {
            ...auth,
            controls: auth.controls.map((control) => ({
              ...control,
              verified: verifiedControls[auth.id]?.[control.id] ?? defaultVerified,
            })),
          };
        }),
      })),
    [groups, verifiedControls]
  );

  const handleToggleExpandGrupo = (grupoId: string) => {
    setExpandedGrupoId((prev) => (prev === grupoId ? null : grupoId));
  };

  const handleToggleExpandAutorizacion = (authId: string) => {
    setExpandedAutorizacionId((prev) => (prev === authId ? null : authId));
  };

  const handleVerificarControl = (authId: string, controlId: GateControlKey) => {
    const auth = groups.flatMap((group) => group.authorizations).find((a) => a.id === authId);
    const defaultVerified = !!auth && isGuardApprovedRequest(auth);
    setVerifiedControls((prev) => ({
      ...prev,
      [authId]: {
        ...prev[authId],
        [controlId]: !(prev[authId]?.[controlId] ?? defaultVerified),
      },
    }));
  };

  const handleObservacionTextChange = (authId: string, controlId: GateControlKey, text: string) => {
    setControlObservations((prev) => ({
      ...prev,
      [authId]: {
        ...prev[authId],
        [controlId]: { photo: null, ...prev[authId]?.[controlId], text },
      },
    }));
  };

  const handleObservacionPhotoChange = (
    authId: string,
    controlId: GateControlKey,
    photo: File | null
  ) => {
    setControlObservations((prev) => ({
      ...prev,
      [authId]: {
        ...prev[authId],
        [controlId]: { text: "", ...prev[authId]?.[controlId], photo },
      },
    }));
  };

  // Los controles de la autorización elegida, con el `verified` ya resuelto contra su default.
  // Incluye tanto los 4 controles "simples" como las patentes (tractor y acoplados).
  const selectedControls = React.useMemo<TransportExitControls | null>(() => {
    if (!selectedAutorizacionId) return null;

    const auth = groupsWithVerification
      .flatMap((group) => group.authorizations)
      .find((a) => a.id === selectedAutorizacionId);
    if (!auth) return null;

    const observations = controlObservations[selectedAutorizacionId] || {};
    const controls: TransportExitControls = {};
    auth.controls.forEach((control) => {
      const isPlate = isPlateControlKey(control.id);
      if (!isExitControlKey(control.id) && !isPlate) return;
      controls[control.id] = {
        verified: control.verified,
        observations: observations[control.id]?.text || "",
        photo: observations[control.id]?.photo || null,
        ...(isPlate ? { plate: control.value || "" } : {}),
      };
    });
    return controls;
  }, [groupsWithVerification, controlObservations, selectedAutorizacionId]);

  React.useEffect(() => {
    onControlsChange(selectedControls);
  }, [selectedControls, onControlsChange]);

  // Al cambiar de autorización seleccionada, la anterior vuelve a exigir su verificación. Las
  // observaciones, en cambio, se conservan: son datos que el guardia escribió a mano y volver a
  // elegir la autorización debe recuperarlos.
  const prevSelectionRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const prevSelection = prevSelectionRef.current;
    if (prevSelection && prevSelection !== selectedAutorizacionId) {
      setVerifiedControls((prev) => {
        if (!prev[prevSelection]) return prev;
        const next = { ...prev };
        delete next[prevSelection];
        return next;
      });
    }
    prevSelectionRef.current = selectedAutorizacionId;
  }, [selectedAutorizacionId]);

  // Cuando la selección cambia —click manual o auto-selección tras aprobar/"Registrar
  // autorización por otro medio"— se abre el grupo dueño de esa autorización. Sin esto, "Registrar
  // por otro medio" deja la fila tildada pero el acordeón sigue cerrado. Se ancla al id (no a
  // `groups`) para no reabrir un grupo que el guardia cerró a mano en un refetch posterior con la
  // misma selección.
  const prevExpandSelectionRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (
      selectedAutorizacionId &&
      selectedAutorizacionId !== prevExpandSelectionRef.current
    ) {
      const ownerGroup = groups.find((group) =>
        group.authorizations.some((auth) => auth.id === selectedAutorizacionId)
      );
      if (ownerGroup) setExpandedGrupoId(ownerGroup.id);
    }
    prevExpandSelectionRef.current = selectedAutorizacionId;
  }, [selectedAutorizacionId, groups]);

  return (
    <div className="transportEmpresaSection">
      <div className="transportAuthHeader">
        <div className="transportAuthHeaderLeft">
          <p>Autorizaciones de egreso</p>
          <span className="transportAuthBadge">{groupsWithVerification.length}</span>
          <Button
            variant="outline"
            size="small"
            onClick={handleRefreshClick}
            disabled={!canRefresh}
            loading={isLoading}
            className="transportAuthRefreshButton"
          >
            <RefreshIcon fontSize="small" />
          </Button>
        </div>
        <ExpandLessIcon style={{ color: "#64748B", cursor: "pointer" }} />
      </div>

      <div className="transportAuthList">
        {isLoading && <div className="transportAuthEmptyState">Buscando autorizaciones…</div>}

        {!isLoading && groupsWithVerification.length === 0 && (
          <div className="transportAuthEmptyState">
            No hay autorizaciones de egreso para esta empresa transportista.
          </div>
        )}

        {groupsWithVerification.map((grupo) => (
          <AutorizacionEgresoGroupCard
            key={grupo.id}
            grupo={grupo}
            isExpanded={expandedGrupoId === grupo.id}
            onToggleExpand={handleToggleExpandGrupo}
            selectedAutorizacionId={selectedAutorizacionId}
            onSelectAutorizacion={onSelectAutorizacion}
            expandedAutorizacionId={expandedAutorizacionId}
            onToggleExpandAutorizacion={handleToggleExpandAutorizacion}
            onReenviar={onResend}
            onRegistrarPorOtroMedio={onRegistrarPorOtroMedio}
            onVerificarControl={handleVerificarControl}
            controlObservations={controlObservations}
            onObservacionTextChange={handleObservacionTextChange}
            onObservacionPhotoChange={handleObservacionPhotoChange}
            disabled={disabled}
          />
        ))}
      </div>

      <Button
        variant="gradient"
        onClick={() => setIsModalOpen(true)}
        className="transportSolicitarAutorizacionButton"
        disabled={disabled}
      >
        Solicitar autorización <AddIcon fontSize="small" style={{ marginLeft: 8 }} />
      </Button>

      <SolicitarAutorizacionEgresoModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        prefillCompanyId={prefillCompanyId}
        prefillVehicles={prefillVehicles}
        prefillRemito={prefillRemito}
        onSubmit={onRequestAuthorization}
      />
    </div>
  );
};

export default AutorizacionesEgresoList;
